// The Image Structure's editor (spec docs/specs/web/library-image-structure.md, PR 2), as live's
// `structure-editor` (read for behaviour; built without a capture, as opening it on live is a
// pencil click): each element's text, its placement and its bin, the elements a part can add,
// and Cancel and Save, which sends every element on the existing `PATCH` route.
import { ActionIcon, Button, CloseButton, Divider, Group, Popover, Stack, Text, TextInput, Textarea, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Grid2x2, Trash } from "lucide-react";
import { useState } from "react";

import {
  getGetAssetAdStructureQueryKey,
  useUpdateAssetAdStructure,
} from "../../api/generated/endpoints/generation/generation";
import type { AdCreativeStructure } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { DetailSection } from "./MediaPreview";
import { TEXT_ELEMENTS, VISUAL_ELEMENTS, type StructureElement } from "./structure";
import { draftOf, missing, updateOf, type EditRow } from "./structure-edit";
import classes from "./ImageStructure.module.css";

export interface StructureEditorProps {
  teamId: string;
  brandId: string;
  assetId: string;
  structure: AdCreativeStructure;
  /** A read of the structure that failed meanwhile: shown, the draft kept. */
  readError: unknown;
  /** Back to the rows: after a save, or on Cancel. */
  onDone: () => void;
}

export function StructureEditor(props: StructureEditorProps) {
  const { teamId, brandId, assetId } = props;
  const queryClient = useQueryClient();
  const [initial] = useState(() => draftOf(props.structure));
  const [draft, setDraft] = useState(initial);
  const save = useUpdateAssetAdStructure({
    mutation: {
      onSuccess: (answer) => {
        const key = getGetAssetAdStructureQueryKey(teamId, brandId, assetId);
        queryClient.setQueryData(key, answer);
        // Every other read keyed under this structure reads it again: the Advanced composer's
        // analysis of a reference (`use-ad-structures.ts`) holds it in a shape of its own.
        void queryClient.invalidateQueries({ queryKey: key, predicate: (query) => query.queryKey.length > key.length });
        props.onDone();
      },
    },
  });
  const saving = save.isPending;
  // Live's Save waits for a change (`isDirty`).
  const changed = JSON.stringify(draft) !== JSON.stringify(initial);
  const action = (
    <Group gap={6} wrap="nowrap">
      <Button size="compact-sm" variant="default" onClick={props.onDone} disabled={saving}>
        Cancel
      </Button>
      <Button
        size="compact-sm"
        onClick={() => save.mutate({ teamId, brandId, sourceAssetId: assetId, data: updateOf(draft) })}
        loading={saving}
        disabled={!changed}
      >
        Save
      </Button>
    </Group>
  );
  return (
    <DetailSection title="Image Structure" action={action}>
      <Stack gap={12}>
        {props.readError === null ? null : <ErrorAlert error={props.readError} />}
        {save.isError ? <ErrorAlert error={save.error} /> : null}
        <Part
          title="Text"
          elements={TEXT_ELEMENTS}
          rows={draft.texts}
          disabled={saving}
          onRows={(texts) => setDraft((current) => ({ ...current, texts }))}
        />
        <Divider className={classes.divider} />
        <Part
          title="Visual"
          elements={VISUAL_ELEMENTS}
          rows={draft.visuals}
          disabled={saving}
          onRows={(visuals) => setDraft((current) => ({ ...current, visuals }))}
        />
      </Stack>
    </DetailSection>
  );
}

interface PartProps<Key extends string> {
  title: string;
  elements: readonly StructureElement<Key>[];
  rows: readonly EditRow<Key>[];
  disabled: boolean;
  onRows: (rows: EditRow<Key>[]) => void;
}

/** A part's rows, live's `StructureEditorRow`s, and its Additional elements. */
function Part<Key extends string>(props: PartProps<Key>) {
  const { elements, rows, onRows } = props;
  const set = (at: number, change: Partial<EditRow<Key>>) =>
    onRows(rows.map((row, index) => (index === at ? { ...row, ...change } : row)));
  const more = missing(elements, rows);
  return (
    <div className={classes.part}>
      <Text className={classes.partTitle}>{props.title}</Text>
      <ul className={classes.rows} aria-label={props.title}>
        {rows.map((row, at) => {
          const element = elements.find((each) => each.key === row.key);
          if (element === undefined) return null;
          return (
            <li key={row.key} className={classes.editRow}>
              <span className={classes.label}>
                <element.icon size={16} strokeWidth={1.5} aria-hidden />
                {element.label}
              </span>
              <PlacementField
                label={element.label}
                value={row.placement}
                disabled={props.disabled}
                onKeep={(placement) => set(at, { placement })}
              />
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={`Remove ${element.label}`}
                disabled={props.disabled}
                onClick={() => onRows(rows.filter((_, index) => index !== at))}
              >
                <Trash size={16} />
              </ActionIcon>
              <TextInput
                className={classes.input}
                size="xs"
                aria-label={element.label}
                placeholder="Enter text..."
                value={row.value}
                disabled={props.disabled}
                onChange={(event) => set(at, { value: event.currentTarget.value })}
              />
            </li>
          );
        })}
      </ul>
      {more.length === 0 ? null : (
        <div className={classes.more}>
          <Text size="xs">Additional elements</Text>
          <Group gap={6}>
            {more.map((element) => (
              <Button
                key={element.key}
                size="compact-xs"
                variant="default"
                leftSection={<element.icon size={14} aria-hidden />}
                disabled={props.disabled}
                onClick={() => onRows([...rows, { key: element.key, value: "", placement: "" }])}
              >
                {element.label}
              </Button>
            ))}
          </Group>
        </div>
      )}
    </div>
  );
}

/** Live's editable placement: its grid button opens "Placement", a text and a tick that keeps it. */
function PlacementField(props: { label: string; value: string; disabled: boolean; onKeep: (value: string) => void }) {
  const [opened, setOpened] = useState(false);
  const [text, setText] = useState(props.value);
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withinPortal={false} trapFocus>
      <Popover.Target>
        {/* Its words in a tooltip while it is shut, as live's `targetTooltipLabel`. */}
        <Tooltip label={props.value} disabled={props.value === "" || opened} multiline maw={240} withArrow>
          <ActionIcon
            variant={props.value === "" ? "subtle" : "light"}
            size="sm"
            aria-label={`Placement of ${props.label}`}
            disabled={props.disabled}
            onClick={() => {
              setText(props.value);
              setOpened((open) => !open);
            }}
          >
            <Grid2x2 size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown className={classes.placementForm}>
        <Group justify="space-between" mb={6}>
          <Text size="sm" fw={600}>
            Placement
          </Text>
          <CloseButton size="sm" aria-label="Close placement" onClick={() => setOpened(false)} />
        </Group>
        <Group align="flex-end" gap={8} wrap="nowrap">
          <Textarea
            className={classes.placementText}
            autosize
            minRows={3}
            maxRows={6}
            aria-label="Placement"
            value={text}
            onChange={(event) => setText(event.currentTarget.value)}
          />
          <ActionIcon
            radius="xl"
            variant="filled"
            aria-label="Keep placement"
            onClick={() => {
              props.onKeep(text);
              setOpened(false);
            }}
          >
            <Check size={18} />
          </ActionIcon>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
